import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Eye, 
  Calendar 
} from 'lucide-react';
import { PropertyBrochure } from '../types';
import apiService from '../services/api';

interface BrochureViewerProps {
  brochures: PropertyBrochure[];
  onRequestDownload?: (brochure: PropertyBrochure) => void;
}

const BrochureViewer: React.FC<BrochureViewerProps> = ({ brochures, onRequestDownload }) => {
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());

  const handleDownload = async (brochure: PropertyBrochure) => {
    if (onRequestDownload) {
      onRequestDownload(brochure);
      return;
    }

    try {
      setDownloadingIds(prev => new Set(prev).add(brochure.id));

      // Create a temporary link to trigger download
      const downloadUrl = apiService.getBrochureDownloadUrl(brochure.id);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = brochure.original_name || brochure.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(brochure.id);
        return newSet;
      });
    }
  };

  const handlePreview = (brochure: PropertyBrochure) => {
    // Open PDF in new tab for preview
    const previewUrl = apiService.getBrochureViewUrl(brochure.id);
    window.open(previewUrl, '_blank');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!brochures || brochures.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <FileText className="w-6 h-6 mr-2 text-blue-600" />
        Property Brochures & Documents
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {brochures.map((brochure) => (
          <div
            key={brochure.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            {/* Brochure Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {brochure.title || brochure.original_name}
                  </h3>
                  <p className="text-sm text-gray-600 truncate">
                    {brochure.original_name}
                  </p>
                </div>
              </div>
            </div>

            {/* Brochure Description */}
            {brochure.description && (
              <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                {brochure.description}
              </p>
            )}

            {/* Brochure Metadata */}
            <div className="grid grid-cols-2 gap-4 mb-4 text-xs text-gray-500">
              <div className="flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                <span>{formatDate(brochure.uploaded_at)}</span>
              </div>
              <div className="flex items-center">
                <span className="mr-1">📄</span>
                <span>{formatFileSize(brochure.file_size || 0)}</span>
              </div>
              <div className="flex items-center">
                <Download className="w-3 h-3 mr-1" />
                <span>{brochure.download_count} downloads</span>
              </div>
              <div className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                <span>PDF</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handlePreview(brochure)}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </button>
              <button
                onClick={() => handleDownload(brochure)}
                disabled={downloadingIds.has(brochure.id)}
                className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingIds.has(brochure.id) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> These brochures contain detailed information about the property including floor plans, 
          amenities, pricing, and specifications. All documents are in PDF format and can be viewed or downloaded.
        </p>
      </div>
    </div>
  );
};

export default BrochureViewer;